import { getRole, Roles } from 'api/tokenStorage';
import { PageHead } from 'ui/kit/PageHead';
import { FormContainer } from 'components/formsV2/formContainer/index';
import { useRemoveUserRole } from 'hooks/useRemoveUserRole';
import type React from 'react';
import { useState } from 'react';
import { ConfirmModal } from 'ui';
import { PasswordRulesHint } from 'components/PasswordRulesHint';
import {
  passwordApiFieldError,
  useChangePassword,
  useSetPassword,
} from 'hooks/usePasswordChange';
import type { FormikHelpers } from 'formik';
import { useAuthSession } from 'hooks/useAuthSession';
import { AUTH_SESSION_QUERY_KEY } from 'hooks/useAuthSession';
import { useQueryClient } from '@tanstack/react-query';
import {
  PASSWORD_MAX_LENGTH,
  confirmPasswordFieldSchema,
  newPasswordFieldSchema,
} from 'schemas/passwordRules';
import * as yup from 'yup';
import { FadeIn } from 'ui/kit/uiMotion';
import { DesktopNotifySettings } from 'components/DesktopNotifyPrompt/DesktopNotifySettings';
import { TutorVkSettings } from 'modules/Teacher/components/TutorVkSettings';
import { PersonalDataChangeRequestSection } from './PersonalDataChangeRequestSection';
import { formatEmailDisplay, formatPhoneDisplay } from './contactUtils';
import s4 from './s4Screen.module.scss';

type PasswordFormValues = {
  old_password: string;
  new_password: string;
  repeat_new_password: string;
};

const changePasswordSchema = yup.object().shape({
  old_password: yup
    .string()
    .required('Введи текущий пароль')
    .max(
      PASSWORD_MAX_LENGTH,
      `Пароль не должен превышать ${PASSWORD_MAX_LENGTH} символов`,
    ),
  new_password: newPasswordFieldSchema({ requiredMessage: 'Введи новый пароль' }).notOneOf(
    [yup.ref('old_password')],
    'Новый пароль должен отличаться от старого',
  ),
  repeat_new_password: confirmPasswordFieldSchema('new_password'),
});

const setPasswordSchema = yup.object().shape({
  new_password: newPasswordFieldSchema({ requiredMessage: 'Введи пароль' }),
  repeat_new_password: confirmPasswordFieldSchema('new_password'),
});

export const Settings: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const removeRole = useRemoveUserRole();
  const roleDict = getRole();
  const roleId = roleDict ? Roles[roleDict as keyof typeof Roles] : undefined;
  const changePasswordRequest = useChangePassword();
  const setPasswordRequest = useSetPassword();
  const { data: session } = useAuthSession();
  const queryClient = useQueryClient();
  const needsSetPassword = session?.has_password === false;

  const initialValues = {
    old_password: '',
    repeat_new_password: '',
    new_password: '',
  };

  const handleSubmit = (values: PasswordFormValues, helpers: FormikHelpers<PasswordFormValues>) => {
    // Ошибки API, относящиеся к полю (неверный текущий пароль, «должен отличаться»),
    // показываем под этим полем, а не всплывашкой.
    const showFieldError = (error: unknown) => {
      const fieldError = passwordApiFieldError(error);
      if (!fieldError) return;
      helpers.setFieldTouched(fieldError.field, true, false);
      helpers.setFieldError(fieldError.field, fieldError.message);
    };
    if (needsSetPassword) {
      setPasswordRequest.mutate(
        {
          new_password: values.new_password,
          repeat_new_password: values.repeat_new_password,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
          },
          onError: showFieldError,
        },
      );
      return;
    }
    changePasswordRequest.mutate(values, { onError: showFieldError });
  };

  return (
    <FadeIn>
      <main className={s4.page}>
        <PageHead crumb={<>Личный кабинет / Настройки</>} title={<>Настройки</>} />

        <section className={s4.card}>
          <h2 className={s4.cardTitle} data-icon="contacts">Контакты и доступ</h2>
          <p className={s4.body}>Актуальные контакты для входа и сообщений о занятиях.</p>
          <div className={s4.row}>
            <div className={s4.cell}>
              <p className={s4.label}>Электронная почта</p>
              <p className={s4.value}>{formatEmailDisplay(session?.email)}</p>
            </div>
            <div className={s4.cell}>
              <p className={s4.label}>Телефон</p>
              <p className={s4.value}>{formatPhoneDisplay(session?.phone)}</p>
            </div>
          </div>
          <p className={s4.muted}>
            Почту и телефон нельзя поменять самостоятельно. Если данные устарели, отправь заявку
            ниже.
          </p>
        </section>

        <FormContainer<PasswordFormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={needsSetPassword ? setPasswordSchema : changePasswordSchema}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize
        >
          {({ values, errors, touched, handleBlur, setFieldValue, setFieldTouched }) => (
            <section className={s4.card}>
              <h2 className={s4.cardTitle} data-icon="lock">Пароль</h2>
              <p className={s4.muted}>
                {needsSetPassword
                  ? 'Задай пароль, если хочешь входить не только по коду.'
                  : 'Оставь поля пустыми, если не хочешь менять пароль.'}
              </p>
              {needsSetPassword ? null : (
                <label className={s4.field}>
                  <span className={s4.label}>Текущий пароль</span>
                  <input
                    className={s4.control}
                    type='password'
                    name='old_password'
                    placeholder='Введи текущий пароль'
                    value={values.old_password}
                    onChange={(event) => {
                      setFieldValue('old_password', event.target.value);
                      if (errors.old_password) setFieldTouched('old_password', false);
                    }}
                    onBlur={handleBlur}
                  />
                  {touched.old_password && errors.old_password ? (
                    <p className={`fig-field-error ${s4.error}`} role='alert'>
                    {errors.old_password}
                  </p>
                  ) : null}
                </label>
              )}
              <div className={s4.row}>
                <label className={s4.cell}>
                  <span className={s4.label}>Новый пароль</span>
                  <input
                    className={s4.control}
                    type='password'
                    name='new_password'
                    placeholder='Введи новый пароль'
                    value={values.new_password}
                    onChange={(event) => {
                      setFieldValue('new_password', event.target.value);
                      if (errors.new_password) setFieldTouched('new_password', false);
                    }}
                    onBlur={handleBlur}
                  />
                  {touched.new_password && errors.new_password ? (
                    <p className={`fig-field-error ${s4.error}`} role='alert'>
                    {errors.new_password}
                  </p>
                  ) : null}
                </label>
                <label className={s4.cell}>
                  <span className={s4.label}>Повтори новый пароль</span>
                  <input
                    className={s4.control}
                    type='password'
                    name='repeat_new_password'
                    placeholder='Повтори пароль'
                    value={values.repeat_new_password}
                    onChange={(event) => {
                      setFieldValue('repeat_new_password', event.target.value);
                      if (errors.repeat_new_password) {
                        setFieldTouched('repeat_new_password', false);
                      }
                    }}
                    onBlur={handleBlur}
                  />
                  {touched.repeat_new_password && errors.repeat_new_password ? (
                    <p className={`fig-field-error ${s4.error}`} role='alert'>
                    {errors.repeat_new_password}
                  </p>
                  ) : null}
                </label>
              </div>
              <PasswordRulesHint />
              <p className={s4.hint}>
                Используй уникальный пароль. Не отправляй его в чате или в поддержку.
              </p>
              <div className={s4.saveRow}>
                <button
                  className={s4.primary}
                  type='submit'
                  disabled={changePasswordRequest.isPending || setPasswordRequest.isPending}
                >
                  {needsSetPassword ? 'Задать пароль' : 'Сохранить изменения'}
                </button>
                <p className={s4.muted}>Изменения пароля применяются после подтверждения.</p>
              </div>
            </section>
          )}
        </FormContainer>

        <section className={s4.card}>
          <h2 className={s4.cardTitle} data-icon="bell">Уведомления</h2>
          <DesktopNotifySettings showTitle={false} />
          <p className={s4.muted}>Уведомления также приходят на почту аккаунта.</p>
          <TutorVkSettings audience='student' />
        </section>

        <PersonalDataChangeRequestSection />

        <section className={s4.card}>
          <h2 className={s4.cardTitle} data-icon="user">Действия с аккаунтом</h2>
          <p className={s4.body}>Ты потеряешь историю и контакты репетиторов.</p>
          <div className={s4.actions}>
            <button type='button' className={s4.secondary} onClick={() => setModalOpen(true)}>
              Удалить
            </button>
          </div>
        </section>

        {modalOpen ? (
          <ConfirmModal
            onOk={() => {
              if (roleId) void removeRole.mutateAsync({ roleId });
            }}
            open={modalOpen}
            title='Удалить аккаунт?'
            text='Вы потеряете историю и контакты учеников. Действие нельзя отменить.'
            okLabel='Удалить'
            danger
            onClose={() => setModalOpen(false)}
          />
        ) : null}
      </main>
    </FadeIn>
  );
};
